// src/pages/TestOCR.jsx — AI OCR API Testing Center
//
// Exercises the aiocr endpoints from the dashboard, with the same request
// shape a third-party integration would send (see
// whatsapp-broker/docs/aiocr_gemini.md and aiocr_sales_decode.md). Same-origin
// on purpose: the calls go through the nginx in front of this app, which
// proxies /api/aiocr*, /api/invoice-ocr*, /api/audio-order*,
// /api/sales-voice-decode and /api/lead-score* to the OCR container, and the
// dashboard's session cookie only travels on this origin.
import React, { useState, useRef, useEffect } from 'react';
import { FiUpload, FiRefreshCw, FiActivity } from 'react-icons/fi';

// The `language` form field of /api/sales-voice-decode: the language that
// description and products come back in. The server accepts more codes than
// this; these are the ones worth a click.
const SALES_LANGUAGES = [
  ['en', 'English'], ['fr', 'French'], ['es', 'Spanish'], ['de', 'German'],
  ['it', 'Italian'], ['pt', 'Portuguese'], ['ar', 'Arabic'], ['hi', 'Hindi'],
  ['ta', 'Tamil'], ['te', 'Telugu'], ['kn', 'Kannada'], ['ml', 'Malayalam'],
  ['mr', 'Marathi'], ['bn', 'Bengali'], ['gu', 'Gujarati'], ['ur', 'Urdu'],
  ['wo', 'Wolof'], ['ff', 'Fula'], ['bm', 'Bambara'], ['fo', 'Fon'], ['yo', 'Yoruba'],
];

const APIS = {
  document: {
    label: 'Document OCR',
    path: '/api/aiocr',
    field: 'file',
    accept: '.pdf,.jpg,.jpeg,.png,.webp',
    types: ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'],
    hint: 'Bank statement, trade licence, VAT certificate, passport / Emirates ID, or any other document.',
  },
  invoice: {
    label: 'Invoice OCR',
    path: '/api/invoice-ocr',
    field: 'file',
    accept: '.pdf,.jpg,.jpeg,.png,.webp',
    types: ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'],
    hint: 'Invoice, bill or receipt — printed or handwritten. Returns supplier, totals and line items.',
  },
  audio: {
    label: 'Voice Order',
    path: '/api/audio-order',
    field: 'audio',
    accept: '.wav,.mp3,.m4a,.ogg,.webm',
    types: ['audio/wav', 'audio/x-wav', 'audio/mpeg', 'audio/mp4', 'audio/x-m4a', 'audio/ogg', 'audio/webm'],
    hint: 'A short voice note. Returns intent (order / report / irrelevant), transcript and a cart.',
  },
  sales: {
    label: 'Voice Sales Decoder',
    path: '/api/sales-voice-decode',
    field: 'audio',
    accept: '.wav,.mp3,.m4a,.ogg,.webm',
    types: ['audio/wav', 'audio/x-wav', 'audio/mpeg', 'audio/mp4', 'audio/x-m4a', 'audio/ogg', 'audio/webm'],
    hint: 'A voice note describing a sale, in any language. Returns a fixed JSON: amount, description and products (in the language you pick), transcript, confidence (0–10), payment method, spoken language.',
    // Gemini only: there is no /api/v2 twin for this endpoint.
    engines: ['v1'],
    doc: 'docs/aiocr_sales_decode.md',
  },
  lead: {
    label: 'Lead Scoring',
    path: '/api/lead-score',
    // Not an upload: the demo form's JSON goes in the request body.
    body: 'json',
    hint: 'The "Book your demo" form submission as JSON. Returns a 0–100 score, band (HOT / WARM / LOW), quality (GENUINE / SUSPICIOUS / SPAM) and the signals behind it. Every call is kept on the Lead Scoring page.',
    doc: 'docs/lead_scoring.md',
  },
};

// A realistic submission to start from; edit any field and re-submit to see
// the score move (try firstName "aaaaa" or businessName "blah blah").
const SAMPLE_LEAD = {
  submittedAt: new Date().toISOString(),
  businessName: 'Sharma Stores',
  firstName: 'Ravi',
  lastName: 'Sharma',
  phone: '+919845012345',
  whatsappNumber: '+919845012345',
  email: 'ravi@sharmastores.in',
  region: 'Karnataka',
  businessType: 'RETAIL_KIRANA',
  businessSize: 'SMB',
  outlets: '2',
  needs: ['ACCEPT_UPI_CARDS', 'BILLING_STOCK'],
  onlineSales: 'WANT_TO',
  description: 'Demo for billing and UPI across 2 shops',
  consent: true,
  brand: 'urban-vyapari',
  market: 'india',
  language: 'en',
  campaignId: '',
  channel: '',
  website: '',
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  utmContent: '',
  utmTerm: '',
  landingPage: 'https://urbanvyapari.in/?lang=en',
  referralName: '',
  referralPhone: '',
  referralSource: '',
};

const isAudioApi = key => APIS[key].field === 'audio';
const isJsonApi = key => APIS[key].body === 'json';

// 'v1' = Gemini (Google AI Studio key), 'v2' = YC Engine (self-hosted Ollama). Same
// request and response shape on both — only the model behind the path differs.
const ENGINES = {
  v1: { label: 'Gemini', prefix: '' },
  v2: { label: 'YC Engine', prefix: '/api/v2' },
};

function endpointFor(apiKey, engine) {
  const api = APIS[apiKey];
  return engine === 'v2' ? api.path.replace('/api', ENGINES.v2.prefix) : api.path;
}

// The Voice Sales Decoder answers a fixed five-key object, so it can be laid
// out as a card instead of leaving the reader to scan JSON. The raw JSON is
// still printed underneath — that is what an integration receives.
function SalesDecodeCard({ result }) {
  const confidence = Number(result.confidence);
  const tone = confidence >= 8 ? 'text-green-700' : confidence >= 5 ? 'text-amber-700' : 'text-red-700';
  const cells = [
    ['Sale amount', result.amount === 'NA' ? 'NA' : Number(result.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })],
    ['Payment method', result.payment_method],
    ['Language', result.language],
    ['Confidence', <span className={`font-semibold ${tone}`}>{result.confidence} / 10</span>],
  ];
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <p className="text-gray-900 mb-3">{result.description}</p>
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        {cells.map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
            <dd className="text-gray-900 mt-0.5">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// The lead score as a card: the number, the band and quality it landed in,
// and the signal table that adds up to it — the JSON is underneath.
function LeadScoreCard({ result }) {
  const bandTone = { HOT: 'text-green-700', WARM: 'text-amber-700', LOW: 'text-gray-600' }[result.band] || 'text-gray-600';
  const qualityTone = { GENUINE: 'text-green-700', SUSPICIOUS: 'text-amber-700', SPAM: 'text-red-700' }[result.quality] || 'text-gray-600';
  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="text-4xl font-extrabold text-gray-900">{result.score}</span>
        <span className={`font-semibold ${bandTone}`}>{result.band}</span>
        <span className={`font-semibold ${qualityTone}`}>{result.quality}</span>
        <span className="text-sm text-gray-500">spam {Math.round((result.spam_probability || 0) * 100)}% · {result.intent}</span>
        <span className="text-sm text-gray-500">{result.action}</span>
      </div>
      <p className="text-gray-900">{result.summary}</p>
      {Array.isArray(result.reasons) && result.reasons.length > 0 && (
        <ul className="list-disc pl-5 text-sm text-gray-700">
          {result.reasons.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
      {Array.isArray(result.flags) && result.flags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {result.flags.map(f => (
            <span key={f} className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">{f}</span>
          ))}
        </div>
      )}
      {Array.isArray(result.signals) && (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-800 text-white text-left">
              <th className="px-2 py-1 font-medium">Signal</th>
              <th className="px-2 py-1 font-medium">Value</th>
              <th className="px-2 py-1 font-medium">Threshold</th>
              <th className="px-2 py-1 font-medium text-right">Pts</th>
            </tr>
          </thead>
          <tbody>
            {result.signals.map(sig => (
              <tr key={sig.signal} className="border-b border-gray-100">
                <td className="px-2 py-1">{sig.signal}</td>
                <td className="px-2 py-1">{sig.value === null || sig.value === undefined ? '-' : String(sig.value)}</td>
                <td className="px-2 py-1 text-gray-500">{sig.threshold}</td>
                <td className={`px-2 py-1 text-right font-semibold ${sig.points ? 'text-violet-700' : 'text-gray-400'}`}>{sig.points ? `+${sig.points}` : '0'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="text-xs text-gray-500">
        {result.raw_points} raw points; {result.reference_points} = 100. {result.id ? `Saved as lead #${result.id}.` : 'Not saved (lead store unavailable).'}
      </p>
    </div>
  );
}

export default function AIOCRTester() {
  const [apiKey, setApiKey] = useState('document');
  const [engine, setEngine] = useState('v1');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [rawCrDr, setRawCrDr] = useState(false);
  const [includeImages, setIncludeImages] = useState(false);
  const [salesLanguage, setSalesLanguage] = useState('en');
  const [jsonBody, setJsonBody] = useState(() => JSON.stringify(SAMPLE_LEAD, null, 2));
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null); // { code, ms }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [health, setHealth] = useState(null);
  const fileInputRef = useRef(null);

  const api = APIS[apiKey];
  const endpoint = endpointFor(apiKey, engine);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Which backend is live and whether it has a Gemini key — the answer to
  // "why is every call a 503" before the first upload.
  const loadHealth = () => {
    fetch('/api/aiocr/health', { cache: 'no-store' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setHealth)
      .catch(err => setHealth({ status: 'unreachable', detail: err.message }));
  };
  useEffect(loadHealth, []);

  const resetForm = () => {
    setFile(null);
    setPreviewUrl('');
    setResult(null);
    setStatus(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const pickApi = key => {
    setApiKey(key);
    // An API that only exists on one engine drags the picker onto it.
    const allowed = APIS[key].engines;
    if (allowed && !allowed.includes(engine)) setEngine(allowed[0]);
    resetForm();
  };

  const handleFileChange = e => {
    const selected = e.target.files[0];
    if (!selected) return;
    // Type sniffing by the browser is unreliable for audio (an .m4a can come
    // through as "" or as audio/x-m4a), so fall back to the extension list.
    const ext = `.${(selected.name.split('.').pop() || '').toLowerCase()}`;
    const okByType = api.types.includes(selected.type);
    const okByExt = api.accept.split(',').includes(ext);
    if (!okByType && !okByExt) {
      setError(`Not a file this API accepts. Allowed: ${api.accept.replace(/\./g, '').replace(/,/g, ', ')}.`);
      return;
    }
    setError('');
    setFile(selected);
    setResult(null);
    setStatus(null);
    setPreviewUrl(selected.type === 'application/pdf' ? '' : URL.createObjectURL(selected));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    let init;
    if (isJsonApi(apiKey)) {
      let parsed;
      try {
        parsed = JSON.parse(jsonBody);
      } catch (err) {
        setError(`The body is not valid JSON: ${err.message}`);
        return;
      }
      init = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(parsed) };
    } else {
      if (!file) {
        setError('Please select a file first');
        return;
      }
      const formData = new FormData();
      formData.append(api.field, file);
      if (apiKey === 'invoice' && includeImages) formData.append('include_images', 'true');
      if (apiKey === 'sales' && salesLanguage !== 'en') formData.append('language', salesLanguage);
      init = { method: 'POST', body: formData };
    }
    setLoading(true);
    setError('');
    setResult(null);
    setStatus(null);

    const url = apiKey === 'document' && rawCrDr ? `${endpoint}?RAWCRDR=true` : endpoint;

    const started = performance.now();
    try {
      const response = await fetch(url, init);
      const ms = Math.round(performance.now() - started);
      let body = null;
      try { body = await response.json(); } catch { /* not JSON */ }
      setStatus({ code: response.status, ms });
      if (!response.ok) {
        // FastAPI puts the real reason in `detail`; a 503 here is almost
        // always "no Gemini key" and says so.
        throw new Error(`${response.status}${body?.detail ? ` — ${body.detail}` : ''}`);
      }
      setResult(body);
    } catch (err) {
      setError(`Request failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // The same call as a curl line, so what works here can be pasted into a
  // third party's integration unchanged (minus the token).
  const curlExample = isJsonApi(apiKey)
    ? [
      `curl -X POST https://maa.ycpay.in${endpoint} \\`,
      `  -H "Authorization: Bearer <access_token>" \\`,
      `  -H "Content-Type: application/json" \\`,
      `  -d @lead.json   # the form submission, as in the body above`,
    ].join('\n')
    : [
      `curl -X POST https://maa.ycpay.in${endpoint}${apiKey === 'document' && rawCrDr ? '?RAWCRDR=true' : ''} \\`,
      `  -H "Authorization: Bearer <access_token>" \\`,
      `  -F "${api.field}=@${file ? file.name : 'your-file' + api.accept.split(',')[0]}"${apiKey === 'invoice' && includeImages ? ' \\\n  -F "include_images=true"' : ''}${apiKey === 'sales' && salesLanguage !== 'en' ? ` \\\n  -F "language=${salesLanguage}"` : ''}`,
    ].join('\n');

  const gemini = health?.gemini;
  const healthTone =
    !health ? 'text-gray-500'
      : health.status === 'healthy' ? 'text-green-700'
        : health.status === 'degraded' ? 'text-amber-700'
          : 'text-red-700';

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        <div className="mx-auto" style={{ maxWidth: '1800px' }}>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <h1 className="text-2xl font-bold text-gray-900">AI OCR API Testing Center</h1>
            <div className={`flex items-center gap-2 text-sm ${healthTone}`}>
              <FiActivity className="w-4 h-4" />
              {!health ? (
                <span>Checking OCR service…</span>
              ) : health.status === 'unreachable' ? (
                <span>OCR service unreachable ({health.detail})</span>
              ) : (
                <span>
                  OCR service {health.status} · {health.provider} · {health.model}
                  {gemini && (
                    <>
                      {' · '}
                      {gemini.configured
                        ? `Gemini key ${gemini.key_hint || ''} (${gemini.source})`
                        : 'no Gemini key — set it in Settings → AI OCR Settings'}
                    </>
                  )}
                </span>
              )}
              <button
                type="button"
                onClick={loadHealth}
                className="ml-1 text-gray-400 hover:text-gray-600"
                title="Re-check"
              >
                <FiRefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 lg:gap-8">
            {/* Left: request */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100">
              <div className="border-b border-gray-200 px-6 py-4">
                <h2 className="text-lg font-semibold text-gray-900">Request</h2>
              </div>
              <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* API picker */}
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">API</div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(APIS).map(([key, def]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => pickApi(key)}
                          className={`py-1.5 px-4 rounded-lg text-sm font-medium border transition-colors ${
                            apiKey === key
                              ? 'bg-[#F96168] border-[#F96168] text-white'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {def.label}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">{api.hint}</p>
                  </div>

                  {/* Engine picker */}
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">Engine</div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(ENGINES).filter(([key]) => !api.engines || api.engines.includes(key)).map(([key, def]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setEngine(key)}
                          className={`py-1.5 px-4 rounded-lg text-sm font-medium border transition-colors ${
                            engine === key
                              ? 'bg-[#F96168] border-[#F96168] text-white'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {def.label}
                        </button>
                      ))}
                    </div>
                    {engine === 'v2' && (
                      <p className="mt-2 text-xs text-gray-500">
                        YC Engine runs on our own hardware — a document can take a minute or two.
                      </p>
                    )}
                    {api.engines && (
                      <p className="mt-2 text-xs text-gray-500">
                        This API runs on Gemini only.
                      </p>
                    )}
                  </div>

                  {/* Options */}
                  {apiKey === 'document' && (
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={rawCrDr} onChange={e => setRawCrDr(e.target.checked)} />
                      Include RawCredits / RawDebits (<code>?RAWCRDR=true</code>, pass-through documents only)
                    </label>
                  )}
                  {apiKey === 'invoice' && (
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={includeImages} onChange={e => setIncludeImages(e.target.checked)} />
                      Return the uploaded file as base64 in <code>imageList</code> (<code>include_images=true</code>)
                    </label>
                  )}

                  {apiKey === 'sales' && (
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      Description &amp; products in
                      <select
                        value={salesLanguage}
                        onChange={e => setSalesLanguage(e.target.value)}
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                      >
                        {SALES_LANGUAGES.map(([code, name]) => (
                          <option key={code} value={code}>{name} ({code})</option>
                        ))}
                      </select>
                      (<code>language</code>)
                    </label>
                  )}

                  {/* Body: the form's JSON for Lead Scoring, a file for everything else */}
                  {isJsonApi(apiKey) ? (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Request body (JSON)</span>
                        <button
                          type="button"
                          onClick={() => setJsonBody(JSON.stringify({ ...SAMPLE_LEAD, submittedAt: new Date().toISOString() }, null, 2))}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Reset to sample
                        </button>
                      </div>
                      <textarea
                        value={jsonBody}
                        onChange={e => setJsonBody(e.target.value)}
                        spellCheck={false}
                        rows={22}
                        className="w-full font-mono text-xs p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F96168]/40"
                      />
                    </div>
                  ) : (
                  <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-8">
                    <FiUpload className="w-12 h-12 text-gray-400 mb-4" />
                    <p className="text-gray-600 mb-4">
                      Upload {(api.accept || '').replace(/\./g, '').split(',').join(', ').toUpperCase()}
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={api.accept}
                      onChange={handleFileChange}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer transition-colors"
                    >
                      Choose File
                    </label>
                    {file && (
                      <p className="mt-3 text-sm text-gray-500">
                        Selected: {file.name} ({(file.size / 1024).toFixed(0)} KB)
                      </p>
                    )}
                  </div>
                  )}

                  {error && <div className="text-red-600 bg-red-50 p-3 rounded-lg text-sm">{error}</div>}

                  <div className="flex space-x-4">
                    <button
                      type="submit"
                      disabled={loading || (!isJsonApi(apiKey) && !file)}
                      className="flex-1 py-3 px-6 bg-[#F96168] hover:bg-[#EA434B] disabled:opacity-50 text-white font-medium rounded-lg transition-colors shadow-sm"
                    >
                      {loading ? 'Processing…' : `POST ${endpoint}`}
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="py-3 px-6 flex items-center justify-center border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg transition-colors"
                    >
                      <FiRefreshCw className="mr-2" /> Reset
                    </button>
                  </div>
                </form>

                {/* Equivalent call for an integration */}
                <div className="mt-8">
                  <h3 className="text-md font-medium text-gray-900 mb-2">Same call from an integration</h3>
                  <pre className="text-xs p-3 bg-gray-50 border border-gray-200 rounded-lg overflow-auto whitespace-pre">{curlExample}</pre>
                  <p className="mt-1 text-xs text-gray-500">
                    Token from <code>POST /api/auth/token</code>. Full contract in <code>{api.doc || 'docs/aiocr_gemini.md'}</code>.
                  </p>
                </div>

                {/* Preview */}
                {file && (
                  <div className="mt-8">
                    <h3 className="text-md font-medium text-gray-900 mb-3">Preview</h3>
                    <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center min-h-[160px]">
                      {isAudioApi(apiKey) ? (
                        <audio controls src={previewUrl} className="w-full px-4" />
                      ) : file.type === 'application/pdf' ? (
                        <div className="p-6 text-center">
                          <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16 mx-auto mb-4" />
                          <p className="text-gray-700">PDF preview not available</p>
                          <p className="text-sm text-gray-500 mt-1">{file.name}</p>
                        </div>
                      ) : (
                        <img src={previewUrl} alt="Document preview" className="object-contain max-h-[400px]" />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: response */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100">
              <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Response</h2>
                {status && (
                  <span
                    className={`text-sm font-medium ${
                      status.code >= 200 && status.code < 300 ? 'text-green-700' : 'text-red-700'
                    }`}
                  >
                    HTTP {status.code} · {(status.ms / 1000).toFixed(1)} s
                  </span>
                )}
              </div>
              <div className="p-6">
                {result ? (
                  <div className="space-y-4">
                    {apiKey === 'sales' && <SalesDecodeCard result={result} />}
                    {apiKey === 'lead' && <LeadScoreCard result={result} />}
                    <div className="bg-gray-50 rounded-lg overflow-hidden">
                      <pre className="text-sm p-4 max-h-[640px] overflow-auto">{JSON.stringify(result, null, 2)}</pre>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center min-h-[300px] text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F96168] mb-4"></div>
                        <p>Processing {isJsonApi(apiKey) ? 'lead' : isAudioApi(apiKey) ? 'voice note' : 'document'}…</p>
                      </>
                    ) : (
                      <p>The JSON response will appear here</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
